class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.dispatchEvent(new CustomEvent("cart-drawer:opened"));
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent("cart-drawer:closed"));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ===== Multi-milestone shipping / gift progress bar ===== */

function updateShippingBar(cartData = null) {
  const bar = document.getElementById("shipping-bar");
  if (!bar) return;

  const shippingThreshold = parseFloat(bar.dataset.shippingThreshold);
  const giftThreshold     = parseFloat(bar.dataset.giftThreshold);

  const giftVariantId = parseInt(bar.dataset.giftVariantId) || 0;

  // Get cart total — prefer live event data, fall back to server-rendered value
  // Exclude the free gift item from the total so it doesn't count toward thresholds
  let cartTotal;
  if (cartData && cartData.cartData) {
    let total = cartData.cartData.total_price;
    if (cartData.cartData.items) {
      for (const item of cartData.cartData.items) {
        if (item.variant_id === giftVariantId && item.properties && item.properties._free_gift === 'true') {
          total -= item.final_line_price;
        }
      }
    }
    cartTotal = total / 100;
    bar.dataset.cartTotal = cartTotal;
  } else {
    cartTotal = parseFloat(bar.dataset.cartTotal);
  }

  const shippingReached = cartTotal >= shippingThreshold;
  const giftReached     = cartTotal >= giftThreshold;

  // ── Header message ──
  const emojiEl = document.getElementById("spb-emoji");
  const msgEl   = document.getElementById("spb-msg");
  const totalEl = document.getElementById("spb-total");

  if (giftReached) {
    emojiEl.textContent = "✨";
    msgEl.innerHTML     = "A Mystery Gift has been added!";
  } else if (shippingReached) {
    const remaining = giftThreshold - cartTotal;
    emojiEl.textContent = "🎁";
    msgEl.innerHTML     = `<span>£${remaining.toFixed(2)}</span> away from a Mystery Gift`;
  } else {
    const remaining = shippingThreshold - cartTotal;
    emojiEl.textContent = "🚚";
    msgEl.innerHTML     = `<span>£${remaining.toFixed(2)}</span> away from Free Shipping`;
  }
  totalEl.textContent = `Cart Total: £${cartTotal.toFixed(2)}`;

  // ── Single progress fill with dynamic gradient ──
  const shippingPos = (shippingThreshold / giftThreshold) * 100;  // e.g. 46.67%
  const overallPct  = Math.min((cartTotal / giftThreshold) * 100, 100);

  const fill = document.getElementById("spb-fill");
  if (!fill) return;

  // Set width
  fill.style.width = overallPct + "%";

  // Dynamic gradient: blue portion up to shipping threshold, purple after
  if (overallPct <= 0) {
    fill.style.background = "#60a5fa";
  } else if (!shippingReached) {
    // All blue — haven't reached shipping yet
    fill.style.background = "linear-gradient(90deg, #60a5fa, #3b82f6)";
  } else {
    // Blue for shipping portion, purple for the rest
    // Calculate where the blue-to-purple transition happens within the filled area
    const transitionPoint = (shippingPos / overallPct) * 100;
    fill.style.background = `linear-gradient(90deg, #60a5fa 0%, #3b82f6 ${transitionPoint}%, #a78bfa ${transitionPoint}%, #8b5cf6 100%)`;
  }

  // ── Position the shipping milestone along the track ──
  const msShipping = document.getElementById("spb-ms-shipping");
  msShipping.style.left = shippingPos + "%";

  // ── Milestone circles ──
  const circleShipping = document.getElementById("spb-circle-shipping");
  const circleGift     = document.getElementById("spb-circle-gift");

  circleShipping.className = "spb-milestone-circle" + (shippingReached ? " reached blue" : "");
  circleGift.className     = "spb-milestone-circle" + (giftReached     ? " reached purple" : "");

  // ── Auto-add / remove mystery gift ──
  if (!cartData || cartData.source !== 'free-gift-refresh') {
    handleFreeGift(cartData, giftReached, bar.dataset.giftVariantId);
  }
}

/* ===== Mystery Gift auto-add / remove ===== */
let freeGiftBusy = false;

function handleFreeGift(cartData, giftReached, giftVariantId) {
  if (!giftVariantId || freeGiftBusy) return;

  const variantId = parseInt(giftVariantId);
  if (isNaN(variantId)) return;

  if (cartData && cartData.cartData && cartData.cartData.items) {
    processGiftLogic(cartData.cartData.items, variantId, giftReached);
  } else {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cartJson => {
        processGiftLogic(cartJson.items || [], variantId, giftReached);
      })
      .catch(err => console.error('Mystery gift cart fetch failed:', err));
  }
}

function processGiftLogic(items, variantId, giftReached) {
  let giftInCart = false;
  let giftLineKey = null;
  let giftQty = 0;

  for (const item of items) {
    if (item.variant_id === variantId && item.properties && item.properties._free_gift === 'true') {
      giftInCart = true;
      giftLineKey = item.key;
      giftQty = item.quantity;
      break;
    }
  }

  if (giftReached && !giftInCart) {
    addFreeGift(variantId);
  } else if (giftReached && giftInCart && giftQty > 1) {
    capFreeGiftQty(giftLineKey);
  } else if (!giftReached && giftInCart && giftLineKey) {
    removeFreeGift(giftLineKey);
  }
}

function addFreeGift(variantId) {
  freeGiftBusy = true;
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{
        id: variantId,
        quantity: 1,
        properties: { _free_gift: 'true' }
      }]
    })
  })
  .then(res => res.json())
  .then(() => refreshCartSections())
  .then(() => { freeGiftBusy = false; })
  .catch(err => {
    console.error('Mystery gift add failed:', err);
    freeGiftBusy = false;
  });
}

function removeFreeGift(lineKey) {
  freeGiftBusy = true;
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: 0 })
  })
  .then(res => res.json())
  .then(() => refreshCartSections())
  .then(() => { freeGiftBusy = false; })
  .catch(err => {
    console.error('Mystery gift remove failed:', err);
    freeGiftBusy = false;
  });
}

function capFreeGiftQty(lineKey) {
  freeGiftBusy = true;
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: 1 })
  })
  .then(r => r.json())
  .then(() => refreshCartSections())
  .then(() => { freeGiftBusy = false; })
  .catch(() => { freeGiftBusy = false; });
}

/**
 * Re-render cart drawer + cart icon bubble using Shopify's section rendering API.
 * This is the same approach Dawn uses internally — fetch fresh HTML for both sections,
 * swap the DOM, then update the shipping bar from live /cart.js data.
 */
function refreshCartSections() {
  const sectionsParam = 'cart-drawer,cart-icon-bubble';
  return fetch(`${window.location.pathname}?sections=${sectionsParam}`)
    .then(r => r.json())
    .then(sections => {
      // Re-render cart drawer inner
      if (sections['cart-drawer']) {
        const drawerDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newDrawer = drawerDoc.querySelector('#CartDrawer');
        const currentDrawer = document.getElementById('CartDrawer');
        if (newDrawer && currentDrawer) {
          currentDrawer.innerHTML = newDrawer.innerHTML;
          // Re-attach overlay close listener (destroyed during innerHTML swap)
          const overlay = currentDrawer.querySelector('#CartDrawer-Overlay');
          if (overlay) {
            overlay.addEventListener('click', () => {
              document.querySelector('cart-drawer').close();
            });
          }
        }
      }

      // Re-render cart icon bubble (updates item count badge)
      if (sections['cart-icon-bubble']) {
        const bubbleDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = bubbleDoc.querySelector('.shopify-section');
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      }

      // Toggle empty state on the cart drawer wrapper
      return fetch('/cart.js');
    })
    .then(r => r.json())
    .then(cartJson => {
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      if (cartDrawerWrapper) {
        cartDrawerWrapper.classList.toggle('is-empty', cartJson.item_count === 0);
      }
      // Update shipping bar UI without re-triggering gift logic
      updateShippingBar({ cartData: cartJson, source: 'free-gift-refresh' });
    });
}

function setupCartUpdateListener() {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && (event.source === 'free-gift' || event.source === 'free-gift-refresh')) return;
    updateShippingBar(event);
  });
}

document.addEventListener("cart-drawer:opened", () => {
  updateShippingBar();
  const klaviyoTeaser = document.querySelector(".kl-teaser-QVqkx9");
  if (klaviyoTeaser) klaviyoTeaser.classList.add("hidden");
  document.body.classList.add("cart-drawer-open");
});

document.addEventListener("cart-drawer:closed", () => {
  const klaviyoTeaser = document.querySelector(".kl-teaser-QVqkx9");
  if (klaviyoTeaser) klaviyoTeaser.classList.remove("hidden");
  document.body.classList.remove("cart-drawer-open");
});

// Initialize — fetch full cart so we can detect free gift on load
function initShippingBar() {
  setupCartUpdateListener();

  const bar = document.getElementById("shipping-bar");
  const giftVariantId = bar ? parseInt(bar.dataset.giftVariantId) || 0 : 0;

  fetch('/cart.js')
    .then(r => r.json())
    .then(cartJson => {
      // Clean up orphaned mystery gifts on page load:
      // If the non-gift total is below the threshold, remove any gift that shouldn't be there.
      if (giftVariantId && cartJson.items) {
        let nonGiftTotal = cartJson.items.reduce((sum, item) => {
          if (item.variant_id === giftVariantId && item.properties && item.properties._free_gift === 'true') {
            return sum;
          }
          return sum + item.final_line_price;
        }, 0) / 100;

        const giftThreshold = bar ? parseFloat(bar.dataset.giftThreshold) : 150;
        const giftItem = cartJson.items.find(
          item => item.variant_id === giftVariantId && item.properties && item.properties._free_gift === 'true'
        );

        if (giftItem && nonGiftTotal < giftThreshold) {
          removeFreeGift(giftItem.key);
          return;
        }
      }

      updateShippingBar({ cartData: cartJson });
    })
    .catch(() => {
      updateShippingBar(); // fallback to data attributes
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShippingBar);
} else {
  initShippingBar();
}
